import type { TransportEvent, TransportMode } from '@poi-planner/shared';
import { formatTimeBudget } from '../utils/format';
import { useUIStore } from '../store/uiStore';
import { useTripStore } from '../store/tripStore';

function truncateName(name: string, maxLen = 16): string {
  return name.length > maxLen ? name.slice(0, maxLen - 1) + '…' : name;
}

const MODE_ICONS: Record<TransportMode, string> = {
  flight: 'flight',
  train: 'train',
  car: 'directions_car',
  ferry: 'directions_boat',
  bus: 'directions_bus',
};

function formatTime12(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
}

interface TransportEventCardProps {
  event: TransportEvent;
}

export function TransportEventCard({ event }: TransportEventCardProps) {
  const openTransportEditor = useUIStore((s) => s.openTransportEditor);
  const deleteTransportEvent = useTripStore((s) => s.deleteTransportEvent);
  const resolveLocation = useTripStore((s) => s.resolveLocation);
  const getPOI = useTripStore((s) => s.getPOI);

  // Handle both new format (origin/destination) and legacy format (originPoiId/destinationPoiId)
  const legacyEvent = event as any;
  const originRef = event.origin ?? (legacyEvent.originPoiId ? { type: 'poi' as const, id: legacyEvent.originPoiId } : null);
  const destRef = event.destination ?? (legacyEvent.destinationPoiId ? { type: 'poi' as const, id: legacyEvent.destinationPoiId } : null);

  const originName = truncateName(originRef ? resolveLocation(originRef).name : 'Unknown');
  const destinationName = truncateName(destRef ? resolveLocation(destRef).name : 'Unknown');

  const handleClick = () => {
    openTransportEditor('edit', event.id);
  };

  return (
    <div
      onClick={handleClick}
      className="group card-transport"
    >
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-lg text-slate-400 bg-slate-400/15 p-1 rounded-md">
            {MODE_ICONS[event.mode]}
          </span>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-bold text-slate-100">
              {originName}
            </span>
            <span className="material-symbols-outlined text-base text-slate-500">
              arrow_forward
            </span>
            <span className="text-sm font-bold text-slate-100">
              {destinationName}
            </span>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteTransportEvent(event.id);
          }}
          className="bg-transparent border-none cursor-pointer text-slate-500 p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:text-danger"
        >
          <span className="material-symbols-outlined text-lg">close</span>
        </button>
      </div>

      <div className="flex items-center gap-3 text-xs text-slate-400">
        {event.departureTime && (
          <span className="flex items-center gap-1">
            <span className="material-symbols-outlined text-sm">schedule</span>
            {formatTime12(event.departureTime)}
          </span>
        )}
        <span className="flex items-center gap-1">
          <span className="material-symbols-outlined text-sm">timer</span>
          {formatTimeBudget(event.duration)}
        </span>
        {event.reference && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-400/15 text-slate-300">
            {event.reference}
          </span>
        )}
      </div>
    </div>
  );
}
